import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  CalendarIcon,
  DateTimePicker,
  DesktopDatePicker,
} from '@mui/x-date-pickers';
import { Controller, useFormContext } from 'react-hook-form';
import { InputRules } from '@/types/inputs';
import { buildInputRulesObject } from '@/utils/buildInputRulesObject';
import { Box } from '@mui/material';

dayjs.extend(utc);
dayjs.extend(timezone);

type SVDateTimePickerProps = {
  label: string;
  name: string;
  dateFormat?: string;
  captureInEstTz?: boolean;
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  shouldDisableDate?: (day: dayjs.Dayjs) => boolean;
  rules?: InputRules;
  includeSeconds?: boolean;
};

export default function SVDateTimePicker({
  label,
  name,
  dateFormat = 'MM/DD/YYYY',
  captureInEstTz = false,
  disabled,
  disableFuture = false,
  disablePast = false,
  minDate,
  maxDate,
  shouldDisableDate,
  rules,
  includeSeconds,
}: SVDateTimePickerProps) {
  const methods = useFormContext();
  const builtRulesObject = buildInputRulesObject(label, rules);
  const DateTimePickerComponent = includeSeconds
    ? DateTimePicker
    : DesktopDatePicker;

  return (
    <Controller
      name={name}
      control={methods.control}
      rules={builtRulesObject}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const dateValue = value ? dayjs(value).tz('America/New_York') : null;
        return (
          <Box sx={{ marginBottom: 2 }}>
            <DateTimePickerComponent
              sx={{ width: '100%' }}
              value={dateValue}
              onChange={
                captureInEstTz
                  ? date => {
                      const newDate = dayjs(date).tz('America/New_York', true);
                      onChange(newDate.format(dateFormat));
                    }
                  : date => {
                      onChange(dayjs(date).format(dateFormat));
                    }
              }
              label={label}
              disabled={disabled}
              disableFuture={disableFuture}
              disablePast={disablePast}
              slots={{ openPickerIcon: () => <CalendarIcon /> }}
              shouldDisableDate={shouldDisableDate}
              maxDate={maxDate}
              minDate={minDate}
              slotProps={{
                textField: {
                  error: Boolean(error?.message),
                  helperText: error?.message,
                  inputProps: { 'data-testid': 'date-time-picker' },
                },
              }}
            />
          </Box>
        );
      }}
    />
  );
}
